import { pinFields, type ExactPin, type JsonObject, type JsonValue } from '../consumer/contract';
import type { MissionTemplateObservation } from '../consumer/reader';
export interface TemplateStage extends JsonObject {
  readonly stageCode: string;
  readonly stageName: string;
  readonly authorizationPoint: boolean;
}
export type TemplateProjection = Readonly<{
  status: MissionTemplateObservation['status'] | 'LOADING';
  authority: 'NON_AUTHORITY';
  pin: ExactPin | null;
  body: JsonObject | null;
  stages: readonly TemplateStage[];
}>;
function object(value: unknown): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Content object required');
}
function freezeJson(value: unknown): JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return Object.freeze(value.map(freezeJson));
  object(value);
  return Object.freeze(
    Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, freezeJson(entry)])),
  );
}
/** Shape validation and lossless display projection only. Backend owns integrity verification. */
export function projectMissionTemplate(
  observation: MissionTemplateObservation | { readonly status: 'LOADING' },
): TemplateProjection {
  const empty = {
    status: observation.status,
    authority: 'NON_AUTHORITY' as const,
    pin: 'query' in observation ? observation.query : null,
    body: null,
    stages: Object.freeze([]),
  };
  if (observation.status !== 'VERIFIED') return Object.freeze(empty);
  try {
    const content: unknown = JSON.parse(
      new TextDecoder('utf-8', { fatal: true }).decode(
        new Uint8Array(observation.snapshot.canonicalContent),
      ),
    );
    object(content);
    const fields = [...pinFields.filter((key) => key !== 'digest'), 'body'];
    if (
      Object.keys(content).length !== fields.length ||
      fields.some((key) => !Object.hasOwn(content, key))
    )
      throw new Error('Invalid canonical content fields');
    for (const key of pinFields)
      if (key !== 'digest' && content[key] !== observation.query[key])
        throw new Error('Content identity mismatch');
    object(content.body);
    if (
      !Array.isArray(content.body.labels) ||
      content.body.labels.some((label) => typeof label !== 'string')
    )
      throw new Error('Source labels required');
    object(content.body.unknownDeclarations);
    object(content.body.unknownExposure);
    object(content.body.policyDependency);
    object(content.body.taskGraphDependency);
    if (!Array.isArray(content.body.stages)) throw new Error('Stages required');
    for (const stage of content.body.stages) {
      object(stage);
      if (
        typeof stage.stageCode !== 'string' ||
        typeof stage.stageName !== 'string' ||
        typeof stage.authorizationPoint !== 'boolean'
      )
        throw new Error('Invalid stage');
      for (const field of [
        'requiredInformationRefs',
        'requiredCapabilityRefs',
        'machineFirstRuleRefs',
        'objectiveTemplate',
        'authoritativeGatingDetermination',
        'exitCriteria',
        'allowedParallelism',
        'operationalMethodAndPriority',
        'timingAndSequenceConstraints',
      ])
        object(stage[field]);
    }
    const body = freezeJson(content.body) as JsonObject;
    return Object.freeze({
      status: 'VERIFIED',
      authority: 'NON_AUTHORITY',
      pin: observation.query,
      body,
      stages: body.stages as readonly TemplateStage[],
    });
  } catch {
    return Object.freeze({ ...empty, status: 'ERROR' });
  }
}
